import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { ErrorMessage } from '@hookform/error-message';

import { _isEmpty, _isNotBlank, _isNotEmpty, _notNil } from '@/littledash';

import type { ID } from '@/model/Common.model';
import type { State } from '@/model/State.model';
import type { MainUser, StudyUser } from '@/model/User.model';

import { missingNameUseEmail, trunc } from '@/helpers';
import { UserPermissions } from '@/referenceData/study/user';
import { isCurrentTeamOwnerWithSettingsAccess } from '@/support/auth';
import { useStudyCreateStep } from '@/utils/studyCreation/useStudyCreate';

import Radio from '@/components/UI/FormElements/Radio';
import Label from '@/components/UI/Label';
import { LabelType } from '@/components/UI/Label/Label';
import RemoveButton from '@/components/UI/RemoveButton';
import SearchSelect from '@/components/UI/SearchSelect';
import ReactSelect from '@/components/UI/Select';

import { isRegisteringOnBehalf } from './Team.utils';

interface TeamUser {
  id: ID;
  api_id: string;
  name: string;
  email: string;
  access: string;
}

interface TeamFormData {
  users: Array<TeamUser>;
  owner: ID | '';
}

interface TeamProps {
  users: Array<StudyUser>;
  owner?: ID;
  author?: StudyUser;
}

const Team: FC<TeamProps> = ({ users = [], owner, author: authorProp }) => {
  const [selectedUsers, setSelectedUsers] = useState<Array<number>>([]);
  const { setStepReady } = useStudyCreateStep();
  const { currentUser, team } = useSelector(({ user: { currentUser }, team: { team } }: State) => ({
    currentUser,
    team,
  }));
  const author: StudyUser | MainUser = authorProp ?? currentUser;

  const [registeringOnBehalfActive, setRegisteringOnBehalfActive] = useState(isRegisteringOnBehalf(owner));

  const {
    control,
    register,
    setValue,
    formState: { errors },
  } = useFormContext<TeamFormData>();
  const { fields, append, remove, replace } = useFieldArray<TeamFormData, 'users', 'keyID'>({
    control,
    keyName: 'keyID',
    name: 'users',
  });

  const teamUsers = useMemo(
    () =>
      (team?.users ?? [])
        .filter((user) => user.pivot.status === 'active' && user.api_id !== author.api_id)
        .map(missingNameUseEmail),
    [team, author]
  );

  useEffect(() => {
    if (_isEmpty(users)) {
      setSelectedUsers([]);
    } else if (_isNotEmpty(teamUsers)) {
      const activeTeamApiIds = new Set(teamUsers.map((user) => user.api_id));
      const { validUsers, validUserIds } = users.reduce<{
        validUsers: Array<StudyUser>;
        validUserIds: Array<number>;
      }>(
        (acc, user) => {
          if (activeTeamApiIds.has(user.api_id)) {
            acc.validUsers.push(user);
            acc.validUserIds.push(Number(user.id));
          }
          return acc;
        },
        { validUsers: [], validUserIds: [] }
      );
      replace(validUsers);
      setSelectedUsers(validUserIds);
    } else {
      setSelectedUsers(users.map((user) => Number(user.id)));
    }
    setStepReady(true);
  }, [setStepReady, users, teamUsers, replace]);

  const sections = useMemo(
    () => [
      {
        name: 'All users',
        items: teamUsers,
      },
    ],
    [teamUsers]
  );

  const handleUserSelect = ({ type, value }: { type: string; value: ID }) => {
    const userById = (user: { id: ID }) => Number(user.id) === Number(value);
    const selectedUser = teamUsers.find(userById);
    if (type === 'ADDED' && selectedUser) {
      const { id, api_id, name, email } = selectedUser;
      append({
        id,
        api_id,
        name,
        email,
        access: 'write',
      });
    } else {
      const indexToRemove = fields.findIndex(userById);
      if (indexToRemove >= 0) {
        remove(indexToRemove);
      }
    }
  };

  const handleClearSelection = () => {
    remove();
    setSelectedUsers([]);
  };

  const handleSelectAll = () => {
    remove();
    setSelectedUsers(teamUsers.map((user: { id: ID }) => Number(user.id)));
    teamUsers.forEach(({ id, api_id, name, email }: { id: ID; api_id: string; name: string; email: string }) => {
      append({
        id,
        api_id,
        name,
        email,
        access: 'write',
      });
    });
  };

  const handleRemoveUser = (index: number) => {
    const userToRemove: TeamUser | undefined = fields[index];
    remove(index);
    if (_notNil(userToRemove)) {
      setSelectedUsers((prev) => prev.filter((id) => id !== Number(userToRemove.id)));
    }
  };

  const getDefaultOwner = (value: ID): { label: string; value: ID } | null => {
    const findOwner = teamUsers.find((user: { id: ID }) => Number(user.id) === Number(value));
    if (findOwner) {
      return {
        label: findOwner.name,
        value: findOwner.id,
      };
    }
    return null;
  };

  const doesUserExistInFields = (id: ID) => fields.some((u: { id: ID }) => Number(u.id) === Number(id));

  const accessLevels = [
    { name: UserPermissions.write, value: UserPermissions.write.toLowerCase() },
    { name: UserPermissions.read, value: UserPermissions.read.toLowerCase() },
  ];

  const isAuthor = currentUser.id === author.id;
  const isAdminWithSettingsAccess = isCurrentTeamOwnerWithSettingsAccess();
  const canManageOwnership = isAuthor || isAdminWithSettingsAccess;
  const authorOwnerLabel =
    isAdminWithSettingsAccess && !isAuthor ? 'Author is the owner of this study' : "I'm the owner of this study";

  return (
    <div className="flex flex-wrap justify-between mw7" data-testid="team-settings__container">
      <div className="w-50 pr3">
        <div className="ui-card">
          <SearchSelect
            selected={selectedUsers}
            setSelected={setSelectedUsers}
            onSelect={handleUserSelect}
            onClearSelection={handleClearSelection}
            onSelectAll={handleSelectAll}
            sections={sections}
            sort
            sortAccessor="name"
          />
        </div>
      </div>
      <div className="w-50 pl3">
        <div className="ui-card overflow-auto h-100" style={{ maxHeight: '508px' }}>
          {_isNotBlank(author?.name) && (
            <div className="pa3" data-testid="team-author-section">
              <div className="flex items-center justify-between pb3">
                <div>
                  <h4 className="near-black f6">{author.name}</h4>
                  <p className="f6">{author.email}</p>
                </div>
                <div>
                  <Label text="Author" type={LabelType.Info} />
                </div>
              </div>
              {canManageOwnership && (
                <div>
                  <Radio
                    name="setOwner"
                    id="registeringForMe"
                    value="me"
                    checked={!registeringOnBehalfActive}
                    onChange={() => {
                      setRegisteringOnBehalfActive(false);
                      setValue('owner', '');
                    }}
                    label={authorOwnerLabel}
                    className="mb2"
                  />
                  <Radio
                    name="setOwner"
                    id="registeringForSomeoneElse"
                    value="other"
                    checked={registeringOnBehalfActive}
                    onChange={() => setRegisteringOnBehalfActive(true)}
                    label="I'm registering it for someone else"
                    className="mb2"
                  />
                  {registeringOnBehalfActive && (
                    <Controller
                      name="owner"
                      control={control}
                      rules={{
                        validate: {
                          ownerCantExistInTeam: (value: ID) =>
                            !doesUserExistInFields(value) || "The owner can't be a team member",
                        },
                      }}
                      render={({ field: { value, onChange } }) => (
                        <>
                          <label>Owner:</label>
                          <ReactSelect
                            className={'db ml4 mt3'}
                            isMulti={false}
                            options={teamUsers.map((user: { id: ID; name: string }) => ({
                              label: user.name,
                              value: user.id,
                            }))}
                            isOptionDisabled={(option: { value: ID }) => doesUserExistInFields(option.value)}
                            value={getDefaultOwner(value)}
                            onChange={(option) => {
                              onChange(_notNil(option) ? (option as { value: ID }).value : '');
                            }}
                          />
                          <ErrorMessage
                            errors={errors}
                            name="owner"
                            render={({ message }: { message: string }) => <p className="f6 red db pt2">{message}</p>}
                          />
                        </>
                      )}
                    />
                  )}
                </div>
              )}
            </div>
          )}
          {fields.map((user, i) => (
            <div
              key={user.keyID}
              className="flex items-center justify-between pa3 bt b--moon-gray hide-child"
              data-testid={`team-user-${user.id}-section`}
            >
              <div>
                {_isNotBlank(user.name) && <h4 className="near-black f6">{user.name}</h4>}
                {_isNotBlank(user.email) && (
                  <p
                    className="f6"
                    data-tooltip-content={(user.email?.length ?? 0) > 30 ? user.email : ''}
                    data-tooltip-id="global-tooltip-id"
                  >
                    {trunc(user.email, 30)}
                  </p>
                )}
                <input {...register(`users.${i}.id`)} type="hidden" defaultValue={String(user.id)} />
                <input {...register(`users.${i}.name`)} type="hidden" defaultValue={user.name} />
                <input {...register(`users.${i}.email`)} type="hidden" defaultValue={user.email} />
                <div className="flex mt2">
                  {accessLevels.map((access) => (
                    <Controller
                      key={access.value}
                      name={`users.${i}.access`}
                      control={control}
                      render={({ field: { onChange, value: fieldValue, name: fieldName } }) => (
                        <Radio
                          name={fieldName}
                          id={`${i}_${access.value}`}
                          checked={fieldValue === access.value}
                          onChange={onChange}
                          value={access.value}
                          label={access.name}
                          className="mr3"
                        />
                      )}
                    />
                  ))}
                </div>
              </div>
              <RemoveButton
                className="child"
                onClick={() => handleRemoveUser(i)}
                testId={`team-user-${user.id}-remove`}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Team;
